import { useRef, type ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { DataTable } from '@/shared/ui/data-table';
import type {
  DataTableColumn,
  DataTableParams,
  DataTableRow,
  DataTableSort,
  DataTableStatus,
} from '@/shared/ui/data-table';
import { styles } from '@/shared/app-list-layout/styles';
import type { AppListLayoutProps, ListRowAction } from '@/shared/app-list-layout/types';

/** The layout's documented default page size, kept when the caller omits one. */
const DEFAULT_PAGE_SIZE = 25;

/**
 * The layout is generic over an unconstrained row type while the engine keys
 * rows by record shape. This adapter is the only place the two vocabularies
 * meet, so it is the only place that needs the structural bridge.
 */
type EngineLayoutProps = AppListLayoutProps<DataTableRow>;

/** Sorting is interactive only when somewhere can receive the new sort. */
function isSortingActionable(props: EngineLayoutProps): boolean {
  return props.onSortChange !== undefined || props.onFetch !== undefined;
}

/** The synthesized trailing column carried by `rowActions`. */
function actionsColumn(
  actions: ReadonlyArray<ListRowAction<DataTableRow>>,
): DataTableColumn<DataTableRow> {
  return {
    id: 'actions',
    header: 'Actions',
    hideable: false,
    sortable: false,
    cell: ({ row }) => <RowActions row={row} actions={actions} />,
  };
}

function RowActions({
  row,
  actions,
}: {
  row: DataTableRow;
  actions: ReadonlyArray<ListRowAction<DataTableRow>>;
}): ReactNode {
  return (
    <div className={styles.rowActions}>
      {actions.map((action) => (
        <Button
          key={action.id}
          variant="secondary"
          size="sm"
          onClick={() => {
            action.onSelect(row);
          }}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Translates the layout's column descriptors into engine columns.
 *
 * A `ListColumn` cell takes the row; the engine's cell context wraps it. The
 * filter/sticky/align/hideable fields are the engine's own vocabulary passed
 * straight through, so a layout page gains a capability by declaring it.
 */
function engineColumns(props: EngineLayoutProps): DataTableColumn<DataTableRow>[] {
  const columns = props.columns.map(
    (column): DataTableColumn<DataTableRow> => ({
      id: column.id,
      header: column.header,
      cell: ({ row, variant }) => column.cell(row, { variant }),
      sortable: column.sortable === true,
      sortDescFirst: false,
      ...(column.filter ? { filter: column.filter } : {}),
      ...(column.sticky ? { sticky: column.sticky } : {}),
      ...(column.align ? { align: column.align } : {}),
      ...(column.primary ? { primary: column.primary } : {}),
      ...(column.hideable === undefined ? {} : { hideable: column.hideable }),
    }),
  );
  return props.rowActions?.length ? [...columns, actionsColumn(props.rowActions)] : columns;
}

/** The controlled sort display the frozen `sortBy`/`sortDirection` props spell. */
function controlledSort(props: EngineLayoutProps): DataTableSort[] | undefined {
  return props.sortBy ? [{ id: props.sortBy, desc: props.sortDirection === 'desc' }] : undefined;
}

/**
 * The engine status for the table.
 *
 * Denial and failure never reach the engine — the layout renders them as
 * surfaces, because a refused read has no table to draw. Loading and empty can
 * be answered by either; the layout keeps both as surfaces so the caller's
 * empty action survives.
 */
function deriveStatus(props: EngineLayoutProps): DataTableStatus {
  if (props.isLoading) return 'loading';
  if (props.isEmpty) return 'empty';
  return 'ready';
}

function sameSort(previous: DataTableSort[], next: DataTableSort[]): boolean {
  const before = previous[0];
  const after = next[0];
  return (before?.id ?? '') === (after?.id ?? '') && (before?.desc ?? false) === (after?.desc ?? false);
}

/** Filters compare by content: a rebuilt object is the same question. */
function sameFilters(previous: Record<string, string>, next: Record<string, string>): boolean {
  const keys = Object.keys(previous);
  if (keys.length !== Object.keys(next).length) return false;
  return keys.every((key) => previous[key] === next[key]);
}

function sameQuery(previous: DataTableParams, next: DataTableParams): boolean {
  return (
    previous.page === next.page &&
    previous.pageSize === next.pageSize &&
    sameSort(previous.sort, next.sort) &&
    sameFilters(previous.filters, next.filters)
  );
}

/**
 * Bridges the engine's single `onFetch` query surface onto the layout's frozen
 * split callbacks, or passes straight through in onFetch mode.
 *
 * The first query is the mount announcement, not a user action: reporting it as
 * a page change would send every rows-mode page to page 1 on first paint. Only
 * a request that differs from the previous one reaches a callback.
 *
 * The whole query is deduped, not just page and sort. A controlled sort click
 * is reported by the engine immediately and again once the caller confirms it
 * in the next render; identical requests are one question, so they are one
 * report in both modes.
 *
 * Known edge, kept deliberately: in rows mode the frozen `onSortChange(id,
 * 'asc' | 'desc')` signature cannot report sort REMOVAL, so on an uncontrolled
 * table whose header passes through asc → desc → none, the third click is
 * swallowed here (nothing reaches `onSortChange`). The engine's own removal
 * state is fine; only the reporting vocabulary is two-valued. Tests pin this
 * in `tests/engine-adapter.test.tsx`.
 */
function useQueryBridge(props: EngineLayoutProps): (params: DataTableParams) => void {
  const previous = useRef<DataTableParams | null>(null);

  return (params: DataTableParams): void => {
    const before = previous.current;
    if (before && sameQuery(before, params)) return;
    previous.current = params;

    if (props.onFetch) {
      props.onFetch(params);
      return;
    }

    if (!before) return;

    if (before.page !== params.page || before.pageSize !== params.pageSize) {
      props.onPageChange?.({ page: params.page, pageSize: params.pageSize });
    }
    if (!sameSort(before.sort, params.sort)) {
      const term = params.sort[0];
      if (term) props.onSortChange?.(term.id, term.desc ? 'desc' : 'asc');
    }
  };
}

/**
 * The layout's table, rendered by the shared engine.
 *
 * Every piece of furniture is capability-gated: a page that supplies no views,
 * no search handler, no column filters, no controlled presentation and no
 * paging callback renders exactly the plain table it rendered before
 * delegation.
 */
export function EngineTable<T>({ props }: { props: AppListLayoutProps<T> }): ReactNode {
  const layout = props as unknown as EngineLayoutProps;
  const onFetch = useQueryBridge(layout);
  const rows = props.rows as unknown as DataTableRow[];
  const caption = `${layout.title} — ${layout.total} total`;
  const toolbar =
    layout.responsive === true ||
    layout.onSearchChange !== undefined ||
    layout.presentation !== undefined ||
    layout.columns.some((column) => column.filter !== undefined);
  // A page that opted into no presentation capability keeps the table this
  // layout has always drawn, at every viewport. The engine's mobile default is
  // cards for pages that show the toggle; a quiet page has no toggle, so a
  // silent switch to cards would be a regression it cannot undo. A page that
  // sets `responsive` has asked for the switch explicitly, so it gets the
  // engine's own default (cards below `sm`, table above) and the toggle.
  const presentation = layout.presentation ?? (toolbar ? undefined : 'table');
  const pageSizeOptions = layout.pageSizeOptions
    ? [...layout.pageSizeOptions]
    : layout.onFetch
      ? undefined
      : [];

  return (
    <DataTable<DataTableRow>
      columns={engineColumns(layout)}
      data={rows}
      total={layout.total}
      status={deriveStatus(layout)}
      onFetch={onFetch}
      rowKey={(row) => layout.getRowId(row)}
      page={layout.page}
      pageSize={layout.pageSize}
      defaultPageSize={layout.pageSize ?? DEFAULT_PAGE_SIZE}
      pageSizeOptions={pageSizeOptions}
      sort={controlledSort(layout)}
      sortControls={isSortingActionable(layout)}
      searchValue={layout.searchValue}
      onSearchChange={layout.onSearchChange}
      onColumnFiltersChange={layout.onColumnFiltersChange}
      views={layout.views ? [...layout.views] : undefined}
      presentation={presentation}
      selectable={layout.selectable}
      bulkActions={layout.bulkActions}
      caption={caption}
      tableCaption={caption}
      showFooter={layout.onFetch !== undefined || layout.onPageChange !== undefined}
      toolbar={toolbar}
      emptyTitle={layout.emptyTitle}
      emptyDescription={layout.emptyDescription}
      noMatchTitle={layout.noMatchTitle}
      noMatchDescription={layout.noMatchDescription}
      errorMessage={layout.errorMessage}
    />
  );
}
